/**
 * TASKS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState, useCallback } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Screen, Section, Button, Tabs } from '../design/primitives';
import { SPACING } from '../design/tokens';
import TaskList from '../components/TaskList';
import CompletedTasksView from '../components/CompletedTasksView';
import TaskForm from '../components/forms/TaskForm';
import { useTaskRefresh } from '../contexts/TaskRefreshContext';

type TabType = 'active' | 'completed';

const TasksPage: React.FC = () => {
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const { refreshKey, refreshTasks } = useTaskRefresh();

  const handleTaskCreated = useCallback(() => {
    refreshTasks();
  }, [refreshTasks]);

  const handleTaskUpdated = useCallback(() => {
    refreshTasks();
  }, [refreshTasks]);

  return (
    <Screen>
      {/* Header */}
      <Section
        title="Tasks"
        action={
          <Button
            variant="primary"
            onClick={() => setIsTaskFormOpen(true)}
            icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
          >
            New Task
          </Button>
        }
      >
        {/* Tabs */}
        <Tabs
          tabs={[
            { id: 'active', label: 'Active Tasks' },
            { id: 'completed', label: 'Completed Tasks' },
          ]}
          activeTab={activeTab}
          onChange={(tab) => setActiveTab(tab as TabType)}
        />

        {/* Tab Content */}
        <div style={{ marginTop: SPACING[16] }}>
          {activeTab === 'active' ? (
            <TaskList key={refreshKey} showCompletedByDefault={false} />
          ) : (
            <CompletedTasksView key={refreshKey} />
          )}
        </div>
      </Section>

      {/* Task Form Modal */}
      <TaskForm
        isOpen={isTaskFormOpen}
        onClose={() => setIsTaskFormOpen(false)}
        onTaskCreated={handleTaskCreated}
        onTaskUpdated={handleTaskUpdated}
      />
    </Screen>
  );
};

export default TasksPage;
